import React, { useCallback } from 'react';
import { SessionHistoryItem } from '../../types';
import { useFormatters } from '../../context/LocalizationContext';
import { useTranslation } from '../../localization/useAppTranslation';
import { ThemedSpinner } from '../ThemedSpinner';
import { logger } from '../../utils/logger';

const moduleLogger = logger.getModule('HistoryPanel');

interface HistoryPanelProps {
  historyItems: SessionHistoryItem[];
  onSelectSession: (sessionId: string) => void;
  onLoadMore: (sessionId: string) => void;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingSession: boolean;
  loadingSessionId: string | null;
  currentSessionId: string;
}

export const HistoryPanel: React.FC<HistoryPanelProps> = ({
  historyItems,
  onSelectSession,
  onLoadMore,
  hasMore,
  isLoading,
  isLoadingSession,
  loadingSessionId,
  currentSessionId,
}) => {
  const { t } = useTranslation('chat');
  const { formatDate } = useFormatters();

  const formatHistoryDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.valueOf())) {
        throw new Error('Invalid date');
      }
      const currentYear = new Date().getFullYear();
      return formatDate(date, date.getFullYear() === currentYear
        ? { month: 'numeric', day: 'numeric' }
        : { year: 'numeric', month: 'numeric', day: 'numeric' });
    } catch (error) {
      moduleLogger.error('Failed to format date', { dateString, error });
      return t('history.invalidDate');
    }
  };

  const formatHistoryTime = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.valueOf())) {
        throw new Error('Invalid date');
      }
      return formatDate(date, {
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch (error) {
      moduleLogger.error('Failed to format time', { dateString, error });
      return t('history.invalidTime');
    }
  };

  const handleItemClick = useCallback(
    (sessionId: string) => {
      if (isLoadingSession) {
        return;
      }

      moduleLogger.debug('History item clicked', { sessionId });
      onSelectSession(sessionId);
    },
    [isLoadingSession, onSelectSession],
  );

  if (historyItems.length === 0 && !isLoading) {
    return <div className="history-empty">{t('history.empty')}</div>;
  }

  return (
    <div
      className={`sidebar-panel-scroll history-list ${isLoadingSession ? 'history-list-loading' : ''}`}
      aria-busy={isLoadingSession}
    >
      {historyItems.map((item) => (
        <div
          key={item.session_id}
          className={`history-item ${loadingSessionId === item.session_id ? 'history-item-loading' : ''}`}
          onClick={() => handleItemClick(item.session_id)}
          role="button"
          tabIndex={isLoadingSession ? -1 : 0}
          aria-disabled={isLoadingSession}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              handleItemClick(item.session_id);
            }
          }}
        >
          <div className="history-item-header">
            <div className="history-item-date">{formatHistoryDate(item.started_at)}</div>
            {loadingSessionId === item.session_id ? (
              <ThemedSpinner size="sm" ariaLabel={t('history.loadingChat')} />
            ) : (
              <div className="history-item-time">{formatHistoryTime(item.started_at)}</div>
            )}
          </div>
          <div className="history-item-description" title={item.description}>
            {item.description}
          </div>
        </div>
      ))}

      {hasMore && (
        <div className="history-load-more-wrap">
          <button
            type="button"
            className="history-load-more"
            onClick={() => onLoadMore(currentSessionId)}
            disabled={isLoading || isLoadingSession}
          >
            {isLoading ? t('history.loading') : t('history.showMore')}
          </button>
        </div>
      )}
    </div>
  );
};